<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        v-model="agendamento.periodo"
                        :min-date="data.additionals.periodoMinDate"
                        :max-date="data.additionals.currentDate"
                        @change="resetFields"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="unidades"
                        label="Unidade executante"
                        show-default-option
                        text-field="unidadeNome"
                        v-model="agendamento.unidadeId"
                        value-field="unidadeId"
                        :validations="validations.unidadeId"
                        @change="resetMedico"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="profissionais"
                        label="Profissional executante"
                        show-default-option
                        text-field="profissionalNome"
                        v-model="agendamento.profissionalId"
                        :validations="validations.profissionalId"
                        value-field="profissionalId"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-select
                        :options="medicos"
                        label="Médico solicitante"
                        show-default-option
                        text-field="medicoNome"
                        default-option-text="Todos"
                        v-model="agendamento.medicoId"
                        value-field="medicoId"
                        :validations="validations.medicoId"
                        @change="resetProfissional"
                    />
                </rkm-col>

                <rkm-col cols="4">
                     <rkm-form-select
                        :options="grupoExames"
                        label="Grupo de exames"
                        show-default-option
                        text-field="grupoExame"
                        v-model="agendamento.grupoExameId"
                        :validations="validations.grupoExameId"
                        value-field="grupoExameId"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="exames"
                        label="Exames"
                        show-default-option
                        text-field="exameNome"
                        v-model="agendamento.exameId"
                        value-field="exameId"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :repository="repositories.paciente"
                        :search-fields="fields.paciente"
                        :text-field="(item) => item.cidadao.nome"
                        label="Paciente"
                        v-model="paciente"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        label="Agendamento"
                        v-model="agendamento.agendamentoNumero"
                        number
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        label="Prontuário"
                        v-model="agendamento.prontuario"
                        number
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        @click="$refs.Form.validate(search)"
                        color="primary"
                        class="float-left"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click.stop="resetForm"
                        color="secondary"
                        class="mr-2 float-left"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-row
            v-if="data.model.agendamentosNotSave"
            class="mt-2 mb-2"
        >
            <rkm-col>
                <template>
                    <rkm-button
                        @click="selectRealizado()"
                        color="info"
                        class="mb-2"
                    >
                        Atribuir todos como realizado
                    </rkm-button>
                </template>

                <template>
                    <rkm-button
                        @click="selectFaltou()"
                        color="danger"
                        class="mb-2"
                    >
                        Atribuir todos como faltou
                    </rkm-button>
                </template>

                <template>
                    <rkm-button
                        @click="cleanSelecionados()"
                        color="secondary"
                        class="mb-2"
                    >
                        Limpar
                    </rkm-button>
                </template>
            </rkm-col>
        </rkm-row>

        <rkm-table
            v-if="data.model.agendamentosNotSave"
            :fields="fields.tables.agendamento"
            :items="data.model.agendamentosNotSave || data.model.agendamentos"
            ref="Table"
            sort-by="agendamento.data"
            hide-filters
        >
            <template v-slot:cell(checkbox)="{ item }">
                <rkm-form-checkbox
                    v-model="item.selecionado"
                    @change="selectVarios"
                />
            </template>

            <template v-slot:cell(pacienteNome)="row">
                <rkm-button
                    class="mr-2"
                    color="secondary"
                    small
                    v-rkm-popover.hover.right="
                        {
                            title: 'Dados cadastrais',
                            html: true,
                            content:
                                '<b>Prontuário:</b> ' + (row.item.agendamento.prontuario || '') + '<br/>' +
                                '<b>Data de Nascimento:</b> ' + row.item.agendamento.dataNascimento + '<br/>' +
                                '<b>Nome da Mãe:</b> ' + row.item.agendamento.paciente.cidadao.nomeMae + '<br/>'
                        }
                    "
                >
                    <rkm-icon icon="user" />
                </rkm-button>

                <span v-if="!row.item.agendamento.paciente.cidadao.sequencialDesconhecido">
                    {{row.item.agendamento.pacienteNome}}

                    <template v-if="row.item.agendamento.paciente.cidadao.nomeSocial">
                        <template v-if="row.item.agendamento.paciente.cidadao.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>({{ row.item.agendamento.paciente.cidadao.nomeSocial }})</strong>
                        </template>

                        <template v-else>
                            <strong>({{ row.item.agendamento.paciente.cidadao.nomeSocial }})</strong>
                        </template>
                    </template>
                </span>

                <span
                    v-else
                    class="text-primary"
                    v-rkm-popover.hover.top="
                        {
                            title: 'Características físicas',
                            content: row.item.agendamento.paciente.cidadao.caracteristicasFisicas
                        }
                    "
                >
                    {{row.item.agendamento.pacienteNome}}
                </span>
            </template>

            <template v-slot:cell(status)="{ item }">

                <rkm-form-select
                    :options="form.status"
                    :label="false"
                    text-field="nome"
                    value-field="id"
                    v-model="item.statusId"
                    show-default-option
                    class="pt-0 mb-0"
                />
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    :id="uniqueId('show-btn')"
                    @click="$refs.Modal.open(item)"
                    title="Detalhamento"
                    small
                    color="primary"
                >
                    <rkm-icon icon="search" />
                </rkm-button>
            </template>
        </rkm-table>

        <Modal
            ref="Modal"
            :data="data"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, object, data, stringCase } from '@/mixins'

// Components.
import Modal from '@/components/exame-imagem/fechamento/components/Modal'

// Moment.
import moment from 'moment'

// Enuns.
import statusFechamento from '@/enums/exame-imagem/fechamento/status'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'

export default {
    components:{ Modal },
    computed: {
        fields() {

            return {
                tables: {
                    agendamento: [
                        { key: 'agendamento.data', label: 'Data do agendamento', class: 'col-max', sortable: true },
                        { key: 'pacienteNome', label: 'Paciente', class: 'col-max', sortable: true },
                        { key: 'agendamento.idadeCompleta', label: 'Idade', class: 'col-max', sortable: true },
                        { key: 'agendamento.prontuario', label: 'Prontuário', class: 'col-max', sortable: true },
                        { key: 'agendamento.agendamentoNumero', label: 'Agendamento', class: 'col-max', sortable: true },
                        { key: 'exameNome', label: 'Exame', class: 'col-max', sortable: true },
                        { key: 'agendamento.profissionalNome', label: 'Profissional executante', class: 'col-max', sortable: true },
                        { key: 'status', label: 'Status', class: 'col-mix', sortable: true },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center'}
                    ]
                },
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        validations() {
            return {
                unidadeId: { required: true },
                profissionalId: { required: true },
                grupoExameId: { required: true },
            }
        },
        unidades(){

            let unidadeOrigem = []

            if (this.agendamento.periodo.start && this.agendamento.periodo.end){

                const start = moment(this.agendamento.periodo.start).subtract(1, 'day')
                const end = moment(this.agendamento.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(agendamento =>
                    moment(agendamento.agendamento.data, 'DD/MM/YYYY').isBetween(start, end))
                        .map(unidade => {

                            this.resetFields()

                            let verifyUnidade = unidadeOrigem.find(u => u.unidadeId === unidade.agendamento.unidadeId)

                            if(!verifyUnidade){

                                unidadeOrigem.push({

                                    unidadeId: unidade.agendamento.unidadeId,
                                    unidadeNome: unidade.agendamento.unidadeNome
                                })
                            }
                        })
            }

            return unidadeOrigem
        },
        medicos(){

            let medicoOrigem = []

            if (this.agendamento.periodo.start && this.agendamento.periodo.end){

                const start = moment(this.agendamento.periodo.start).subtract(1, 'day')
                const end = moment(this.agendamento.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(u => moment(u.agendamento.data, 'DD/MM/YYYY').isBetween(start, end)
                    && u.unidadeId === this.agendamento.unidadeId)
                    .map(medico => {

                        if (medicoOrigem){

                            let verifyMedico = medicoOrigem.find(u => u.medicoId === medico.agendamento.medicoId)

                            if(!verifyMedico){

                                medicoOrigem.push({

                                    medicoId: medico.agendamento.medicoId,
                                    medicoNome: medico.agendamento.medicoNome
                                })
                            }
                        }
                    })

                medicoOrigem.sort((a,b) => (a.medicoNome > b.medicoNome) ? 1 : ((b.medicoNome > a.medicoNome) ? -1 : 0));
            }

            return medicoOrigem
        },
        profissionais(){

            let profissionalOrigem = []

            if (this.agendamento.periodo.start && this.agendamento.periodo.end){

                const start = moment(this.agendamento.periodo.start).subtract(1, 'day')
                const end = moment(this.agendamento.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(u => moment(u.agendamento.data, 'DD/MM/YYYY').isBetween(start, end)
                    && u.unidadeId === this.agendamento.unidadeId)
                        .map(prof => {

                            if (profissionalOrigem){

                                let verifyProfissional = profissionalOrigem.find(u => u.profissionalId === prof.profissionalId)

                                if (!verifyProfissional) {

                                    profissionalOrigem.push({

                                        profissionalId: prof.agendamento.profissionalId,
                                        profissionalNome: prof.agendamento.profissionalNome,
                                    })
                                }
                            }
                        })

                profissionalOrigem.sort((a,b) => (a.profissionalNome > b.profissionalNome) ? 1 : ((b.profissionalNome > a.profissionalNome) ? -1 : 0));
            }

            return profissionalOrigem
        },
        grupoExames(){

            let grupoExameOrigem = []

            if (this.agendamento.periodo.start && this.agendamento.periodo.end){

                const start = moment(this.agendamento.periodo.start).subtract(1, 'day')
                const end = moment(this.agendamento.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(g => moment(g.agendamento.data, 'DD/MM/YYYY').isBetween(start, end)
                    && g.unidadeId === this.agendamento.unidadeId
                    && g.profissionalId === this.agendamento.profissionalId)
                        .map(grupo => {

                            if(grupoExameOrigem){

                                let verifyGrupoExame = grupoExameOrigem.find(e => e.grupoExameId === grupo.grupoExameId)

                                if(!verifyGrupoExame){

                                    grupoExameOrigem.push({

                                        grupoExameId: grupo.grupoExameId,
                                        grupoExame: grupo.agendamento.grupoExame
                                    })
                                }
                            }
                        })

                grupoExameOrigem.sort((a,b) => (a.grupoExame > b.grupoExame) ? 1 : ((b.grupoExame > a.grupoExame) ? -1 : 0));
            }

            return grupoExameOrigem
        },
        exames(){

            let exameOrigem = []

            if (this.data.model.agendamentos) {

                this.data.model.agendamentos.filter(e => e.unidadeId === this.agendamento.unidadeId
                    && e.profissionalId === this.agendamento.profissionalId
                    && e.grupoExameId === this.agendamento.grupoExameId)
                        .map(exame => {

                            if (exameOrigem) {

                                let verifyExame = exameOrigem.find(u => u.exameId === exame.exameId)

                                if (!verifyExame) {

                                    exameOrigem.push({

                                        exameId: exame.exameId,
                                        exameNome: exame.exameNome,
                                    })
                                }
                            }

                        })

                exameOrigem.sort((a,b) => (a.exameNome > b.exameNome) ? 1 : ((b.exameNome > a.exameNome) ? -1 : 0));
            }

            return exameOrigem
        },
    },
    created() {

        this.repositories = {
            fechamento: this.getConectaSusRepository('exameImagem.fechamento'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        },
        this.setDefaultValues();
    },
    data() {
        return {
            selecionados: false,
            agendamento: {
                agendamentoId: null,
                agendamentoNumero: null,
                exameId: null,
                grupoExameId: null,
                medicoId: null,
                pacienteId: null,
                nomePaciente: '',
                periodo: {
                    start: null,
                    end: null,
                },
                profissionalId: null,
                prontuario: null,
                statusId: null,
                unidadeId: null,
            },
            enums:{
                statusFechamento,
                preferenciaNome
            },
            dataAtual: '',
            paciente: null,
            idPaciente: null,
            form: {},
            model: {},
            additionals: {},
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.fechamento.list()
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.dataAtual = data.dataAtual

                    this.setDefaultValues()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        sendLancancamento(row){

            this.idPaciente = row.item.idPaciente

            const confirm = async () => {

                this.$emit('handleAtualizaPaciente', this.idPaciente)
            }

            const cancel = async () => {

                this.$emit('handleExitLancamento')
            }

            const msg = 'Você será redirecionado para outra página. As informações não salvas serão perdidas.' +
                ' Deseja continuar?'

                this.showConfirmation(msg, confirm, cancel)

            return false
        },
        selectRealizado(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'No status de todos os exames, será atribuído REALIZADO. Deseja continuar?',
                    async () => this.data.model.agendamentosNotSave.map((item) => {

                        item.statusId = this.enums.statusFechamento.REALIZADO
                    })
                )

            } else {

                this.showConfirmation(
                    'No status de todos os exames, será atribuído REALIZADO. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = this.enums.statusFechamento.REALIZADO
                    })
                )
            }
        },
        selectFaltou(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'No status de todos os exames, será atribuído FALTOU. Deseja continuar?',
                    async () => this.data.model.agendamentosNotSave.map((item) => {

                        item.statusId = this.enums.statusFechamento.FALTOU
                    })
                )

            } else {

                this.showConfirmation(
                    'No status de todos os exames, será atribuído FALTOU. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = this.enums.statusFechamento.FALTOU
                    })
                )
            }
        },
        cleanSelecionados(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'O status de todos os exames serão alterados para Selecione. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = null
                    })
                )
            } else {

                this.showConfirmation(
                    'O status de todos os exames serão alterados para Selecione. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = null
                    })
                )
            }
        },
        search(){

            let buscaAgendamento = this.data.model.agendamentos

            if(this.agendamento.agendamentoNumero){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.agendamentoNumero == this.agendamento.agendamentoNumero)
            }

            if(this.agendamento.exameId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.exameId == this.agendamento.exameId)
            }

            if(this.agendamento.grupoExameId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.grupoExameId == this.agendamento.grupoExameId)
            }

            if(this.paciente){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.pacienteId == this.paciente.id)
            }

            if (this.agendamento.periodo.start && this.agendamento.periodo.end){

                const start = moment(this.agendamento.periodo.start).subtract(1, 'day')
                const end = moment(this.agendamento.periodo.end).add(1, 'day')

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    moment(agendamento.agendamento.data, 'DD/MM/YYYY').isBetween(start, end))
            }

            if(this.agendamento.profissionalId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.profissionalId == this.agendamento.profissionalId)
            }

            if(this.agendamento.medicoId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.medicoId == this.agendamento.medicoId)
            }

            if(this.agendamento.prontuario){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.prontuario == this.agendamento.prontuario)
            }

            if(this.agendamento.unidadeId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamento.unidadeId == this.agendamento.unidadeId)
            }

            this.data.model.agendamentosNotSave = buscaAgendamento
        },
        resetFields(){

            this.agendamento.unidadeId = null
            this.agendamento.medicoId = null
            this.agendamento.profissionalId = null
            this.agendamento.grupoExameId = null
            this.agendamento.exameId = null
            this.data.model.agendamentosNotSave = null
        },
        resetMedico(){
            this.agendamento.profissionalId = null
            this.agendamento.grupoExameId = null
            this.agendamento.exameId = null
            this.agendamento.medicoId = null
            this.data.model.agendamentosNotSave = null
        },
        resetProfissional(){

            this.agendamento.grupoExameId = null
            this.agendamento.exameId = null
            this.data.model.agendamentosNotSave = null
        },
        resetForm() {

            this.agendamento.agendamentoNumero = null
            this.agendamento.exameId = null
            this.agendamento.grupoExameId = null
            this.agendamento.medicoId = null
            this.paciente = null
            this.agendamento.profissionalId = null
            this.agendamento.prontuario = null
            this.agendamento.unidadeId = null

            this.data.model.agendamentosNotSave = null

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.data.model.agendamentos.map((item) => {

                    item.statusId = null
                })

            } else {

                this.data.model.agendamentos.map((item) => {

                    item.statusId = null
                })
            }

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()
                    this.$refs.Form.resetValidator()
                })
        },
        setDefaultValues() {
            this.form = this.data.form;
            this.model = this.data.model;
            this.dataAtual = this.data.dataAtual;

            this.agendamento.periodo.start = moment(this.form.dataAtual, 'DD/MM/YYYY').toDate()
            this.agendamento.periodo.end = moment(this.form.dataAtual, 'DD/MM/YYYY').toDate()
        },
    },
    validate() {

        return this.$refs.Form.validate()
    },
    mixins: [repository, error, object, data, stringCase],
    props: {
        data: { type: Object, required: true }
    }
}
</script>

<style scoped>
    .selectPaciente {
        cursor: pointer;
    }
</style>